// MemberCarte.jsx — Carte de membre digitale (/espace/carte)
// Équivalent web de l'écran mobile (member)/carte.tsx : carte brandée + QR + n° adhérent.
import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useOutletContext } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Info, BadgeCheck, Loader2 } from 'lucide-react';

const ROLE_LABELS = {
  president: 'Président', vice_president: 'Vice-président', secretaire: 'Secrétaire',
  vice_secretaire: 'Vice-secrétaire', tresorier: 'Trésorier', membre: 'Membre',
};

export default function MemberCarte() {
  const { asso } = useOutletContext();
  const { user } = useAuth();
  const [adhesion, setAdhesion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/adhesions-ref/me');
        setAdhesion(data.find((a) => a.statut === 'active') || data[0] || null);
      } catch { /* silencieux */ }
      finally { setLoading(false); }
    })();
  }, []);

  const numero = adhesion?.numero_adherent || (user?.id || '').slice(0, 10).toUpperCase();
  const qrValue = `ASSOPERF:MEMBER:${user?.id}:${user?.asso_id || ''}`;
  const initials = (asso?.nom || 'A').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-lg mx-auto" data-testid="carte-page">
      <div className="mb-6 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Ma carte</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Carte de membre</h1>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : (
        <>
          {/* Carte brandée */}
          <div
            className="relative overflow-hidden rounded-3xl p-6 text-white shadow-xl slide-up"
            style={{ background: 'var(--theme-primary)', minHeight: 300 }}
            data-testid="carte-membre-card"
          >
            <div className="absolute -top-10 -right-8 w-40 h-40 rounded-full bg-white/10" />
            <div className="absolute -bottom-16 -left-6 w-40 h-40 rounded-full bg-white/5" />

            <div className="relative flex items-start justify-between">
              <div>
                <div className="text-[10px] font-black tracking-[0.16em] opacity-90">CARTE DE MEMBRE</div>
                <div className="font-display text-xl font-black mt-1 truncate max-w-[220px]">{asso?.nom || user?.asso_nom}</div>
              </div>
              {asso?.logo_url ? (
                <img src={asso.logo_url} alt="" className="w-11 h-11 rounded-xl object-cover bg-white/20" />
              ) : (
                <div className="w-11 h-11 rounded-xl grid place-items-center bg-white/20 font-display font-black text-lg">{initials}</div>
              )}
            </div>

            <div className="relative mt-8">
              <div className="font-display text-2xl sm:text-3xl font-black">{user?.prenom} {user?.nom}</div>
              <div className="text-sm font-semibold opacity-90 mt-0.5">{ROLE_LABELS[user?.role] || 'Membre'}</div>
              {adhesion?.label && (
                <div className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold bg-white/20 rounded-full px-2.5 py-1">
                  <BadgeCheck className="w-3.5 h-3.5" /> {adhesion.label}
                </div>
              )}
            </div>

            <div className="relative flex items-center gap-4 mt-8">
              <div className="bg-white p-2.5 rounded-xl shrink-0">
                <QRCodeSVG value={qrValue} size={92} level="M" data-testid="carte-qr" />
              </div>
              <div className="min-w-0">
                <div className="text-[9px] font-black tracking-[0.14em] opacity-80">N° ADHÉRENT</div>
                <div className="font-mono text-sm font-semibold mt-0.5 truncate" data-testid="carte-numero">{numero}</div>
                {adhesion?.date_fin && (
                  <>
                    <div className="text-[9px] font-black tracking-[0.14em] opacity-80 mt-2">VALIDE JUSQU'AU</div>
                    <div className="text-sm font-semibold">{new Date(adhesion.date_fin).toLocaleDateString('fr-FR')}</div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2.5 mt-5 px-1 text-[#475569]">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#94A3B8]" />
            <p className="text-sm">Présentez cette carte à l'entrée de vos événements pour être identifié par le bureau.</p>
          </div>
        </>
      )}
    </div>
  );
}
